import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Navigation, Loader2, MapPin, Home, Trash2, Plus } from 'lucide-react'
import { Section } from '@/components/Section'
import { useLocationStore, locKey, type SavedLocation } from '@/store/location'
import { getCurrentPosition } from '@/lib/geo/location'
import { resolveLocation, OutOfCoverageError } from '@/lib/nws/client'
import { useGeocodeSearch } from './useGeocodeSearch'
import './locations.css'

/**
 * Location / city selector — search US places, switch between saved locations, mark a
 * home, or use the device GPS. Setting the active location re-drives the whole app (it
 * writes the single `saved` in the location store).
 */
export function LocationsView() {
  const nav = useNavigate()
  const saved = useLocationStore((s) => s.saved)
  const list = useLocationStore((s) => s.list)
  const homeKey = useLocationStore((s) => s.homeKey)
  const setSaved = useLocationStore((s) => s.setSaved)
  const removeLocation = useLocationStore((s) => s.removeLocation)
  const setHome = useLocationStore((s) => s.setHome)

  const [query, setQuery] = useState('')
  const { data: results, isFetching, isError, term } = useGeocodeSearch(query)
  const [locating, setLocating] = useState(false)
  const [geoErr, setGeoErr] = useState<string | null>(null)

  const choose = (loc: SavedLocation) => {
    setSaved(loc)
    nav('/')
  }

  const useMyLocation = async () => {
    setLocating(true)
    setGeoErr(null)
    try {
      const { lat, lon } = await getCurrentPosition()
      const r = await resolveLocation(lat, lon) // NWS → "City, ST"; throws if non-US
      choose({ lat: r.lat, lon: r.lon, label: r.label })
    } catch (e) {
      setGeoErr(
        e instanceof OutOfCoverageError
          ? 'That spot is outside US forecast coverage.'
          : e instanceof Error
            ? e.message
            : 'Could not locate you.',
      )
    } finally {
      setLocating(false)
    }
  }

  const showResults = term.length >= 2

  return (
    <div className="locations">
      <header className="view-head">
        <h1 className="view-title">Locations</h1>
        <p className="view-sub">Search US cities, save your places, switch between them.</p>
      </header>

      <div className="loc-search">
        <Search size={17} className="loc-search__icon" />
        <input
          className="loc-search__input"
          type="text"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Search a US city or place"
          placeholder="Search a US city or place…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        {query && (
          <button className="loc-search__clear" aria-label="Clear search" onClick={() => setQuery('')}>
            <X size={16} />
          </button>
        )}
      </div>

      {showResults && (
        <div className="loc-results card" role="status" aria-live="polite" aria-busy={isFetching}>
          {isFetching ? (
            <div className="loc-state">
              <Loader2 size={16} className="spin" /> Searching…
            </div>
          ) : isError ? (
            <div className="loc-state">Search failed — check your connection.</div>
          ) : results && results.length > 0 ? (
            results.map((r) => (
              <button key={locKey(r)} className="loc-result" onClick={() => choose(r)}>
                <MapPin size={16} className="loc-result__icon" />
                <span className="loc-result__label">{r.label}</span>
                <Plus size={16} className="loc-result__add" />
              </button>
            ))
          ) : (
            <div className="loc-state">No US places found for “{term}”.</div>
          )}
        </div>
      )}

      <button className="loc-gps" onClick={useMyLocation} disabled={locating}>
        {locating ? <Loader2 size={17} className="spin" /> : <Navigation size={17} />}
        <span>Use my location</span>
      </button>
      {geoErr && <p className="loc-err">{geoErr}</p>}

      <Section label="Saved locations">
        {list.length === 0 ? (
          <p className="loc-empty">No saved places yet — search above or use your location.</p>
        ) : (
          <div className="loc-saved">
            {list.map((loc) => {
              const key = locKey(loc)
              const isActive = !!saved && locKey(saved) === key
              const isHome = homeKey === key
              return (
                <div key={key} className={`loc-item${isActive ? ' is-active' : ''}`}>
                  <button className="loc-item__main" onClick={() => choose(loc)}>
                    <span className="loc-item__pin">
                      {isHome ? <Home size={16} /> : <MapPin size={16} />}
                    </span>
                    <span className="loc-item__text">
                      <span className="loc-item__label">{loc.label}</span>
                      <span className="loc-item__meta">
                        <span className="num">
                          {loc.lat.toFixed(3)}, {loc.lon.toFixed(3)}
                        </span>
                        {isActive && <span className="loc-item__badge">Active</span>}
                      </span>
                    </span>
                  </button>
                  <div className="loc-item__actions">
                    <button
                      className={`iconbtn${isHome ? ' is-home' : ''}`}
                      aria-label={isHome ? 'Home location' : `Set ${loc.label} as home`}
                      aria-pressed={isHome}
                      onClick={() => setHome(key)}
                      disabled={isHome}
                    >
                      <Home size={15} />
                    </button>
                    <button
                      className="iconbtn"
                      aria-label={`Remove ${loc.label}`}
                      onClick={() => removeLocation(key)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>
    </div>
  )
}
